import React, { useState, useEffect } from 'react';
import { getUserShares, deleteShareLink } from '../services/sharing';
import { Calendar, Clock, Lock, Trash2, Users, Copy, ChevronLeft, ChevronRight } from 'lucide-react';

const ManageShares = () => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items per page
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const fetchShares = async () => {
      try {
        setLoading(true);
        const userShares = await getUserShares();
        setShares(userShares);
        
        // Calculate total pages
        const totalPages = Math.ceil(userShares.length / itemsPerPage);
        setTotalPages(totalPages);
        
        // Reset to page 1 if current page exceeds total pages
        if (currentPage > totalPages && totalPages > 0) {
          setCurrentPage(1);
        }
      } catch (err) {
        console.error('Error fetching shares:', err);
        setError('Failed to load your shared links');
      } finally {
        setLoading(false);
      }
    };

    fetchShares();
  }, [currentPage, itemsPerPage]);

  const handleDeleteShare = async (shareId) => {
    try {
      // First update UI optimistically
      setShares(shares.filter(share => share._id !== shareId));
      
      // Recalculate pagination
      const newSharesCount = shares.length - 1;
      const newTotalPages = Math.ceil(newSharesCount / itemsPerPage);
      setTotalPages(newTotalPages);
      
      // Adjust current page if needed
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
      
      // Then perform the actual deletion
      await deleteShareLink(shareId);
    } catch (err) {
      console.error('Error deleting share:', err);
      setError('Failed to delete share link. Please try again.');
      
      // If deletion fails, refresh the shares to ensure UI is in sync
      try {
        const userShares = await getUserShares();
        setShares(userShares);
      } catch (refreshErr) {
        console.error('Error refreshing shares:', refreshErr);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generateShareUrl = (shareId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/shared/${shareId}`;
  };

const copyShareUrl = (shareId) => {
  const url = generateShareUrl(shareId);
  
  // Try modern clipboard API first, fallback to older method
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(shareId);
      setTimeout(() => setCopiedId(null), 3000);
    }).catch(() => {
      // Fallback if clipboard API fails
      fallbackCopyTextToClipboard(url, shareId);
    });
  } else {
    // Fallback for older browsers or HTTP connections
    fallbackCopyTextToClipboard(url, shareId);
  }
};

// Fallback function for copying text
const fallbackCopyTextToClipboard = (text, shareId) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  // Apply necessary styles to make the textarea non-intrusive
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px'; // Move it off-screen
  textArea.style.left = '-9999px';
  textArea.style.opacity = '0'; // Make it invisible
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  let successful = false;
  try {
    successful = document.execCommand('copy');
    console.log('Fallback copy result:', successful); // Keep this for debugging
  } catch (err) {
    console.error('Failed to copy text with execCommand: ', err);
  }
  
  document.body.removeChild(textArea);

  // --- KEY CHANGE ---
  // Provide user feedback regardless of the outcome of execCommand.
  // The user clicked, so we show them the "Copied!" state. They will
  // find out if it failed when they try to paste. This is better
  // UX than silently failing.
  setCopiedId(shareId);
  setTimeout(() => setCopiedId(null), 3000);

  // Optional: You could show an error if it failed, but for consistency,
  // showing the "copied" state is often preferred.
  if (!successful) {
    console.warn('Copy command was not successful. Text may not be in the clipboard.');
    // Here you could trigger a toast notification like "Could not copy text".
  }
};

  // Function to display a user-friendly title
  const displayTitle = (share) => {
    if (!share) return "";
    
    const itemType = share.itemType || '';
    const itemTitle = share.itemDetails?.title || 'Untitled';
    const projectTitle = share.itemDetails?.projectTitle || '';
    
    return (
      <div>
        <div className="font-medium">
          {itemType === 'view' ? 'View' : 'Flow'}: {itemTitle}
        </div>
        {projectTitle && (
          <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">
            {projectTitle}
          </div>
        )}
      </div>
    );
  };

  // Pagination calculation
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedShares = shares.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination controls
  const generatePageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show ellipsis when there are many pages
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);
      
      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) {
          pageNumbers.push('...');
        }
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pageNumbers.push('...');
        }
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="py-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading shared links...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="py-4 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="py-4 text-center">
          <p className="text-gray-600">You haven't shared any content yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h3 className="text-lg font-semibold flex items-center mb-4">
        <Users className="w-5 h-5 mr-2" />
        Manage Shared Links ({shares.length})
      </h3>
      
      <div className="space-y-4">
        {displayedShares.map(share => {
          const isExpired = share.expiresAt && new Date() > new Date(share.expiresAt);
          const isCopied = copiedId === share._id;
          
          return (
            <div 
              key={share._id} 
              className={`p-4 border rounded ${isExpired ? 'bg-gray-100' : 'bg-white'}`}
            >
              <div className="flex justify-between items-start">
                <div>
                  {displayTitle(share)}
                  
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    <p className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" /> 
                      Created: {formatDate(share.created)}
                    </p>
                    
                    <p className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" /> 
                      Expires: {formatDate(share.expiresAt)}
                    </p>
                    
                    {share.requirePassword && (
                      <p className="flex items-center">
                        <Lock className="w-4 h-4 mr-1" /> 
                        Password protected
                      </p>
                    )}
                    
                    <p>
                      Access count: {share.accessCount || 0}
                    </p>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => copyShareUrl(share._id)}
                    className={`px-3 py-1 ${isCopied ? 'bg-green-500' : 'bg-black'} text-white text-sm rounded hover:bg-gray-800 transition-colors flex items-center`}
                    disabled={isExpired}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">{isCopied ? 'Copied!' : 'Copy Link'}</span>
                  </button>
                  
                  <button
                    onClick={() => handleDeleteShare(share._id)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete share link"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
    {/* Pagination Controls */}
    {totalPages > 1 && (
      <div className="mt-6 flex items-center justify-between">
        
        {/* This text will now be hidden on mobile screens */}
        <div className="hidden sm:block text-sm text-gray-700">
          Showing {startIndex + 1} to {Math.min(endIndex, shares.length)} of {shares.length} shares
        </div>
        
        {/* Use justify-center on mobile and justify-end on larger screens */}
        <div className="flex items-center space-x-2 w-full sm:w-auto justify-center sm:justify-end">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="p-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <div className="flex space-x-1">
            {generatePageNumbers().map((pageNumber, index) => (
              <React.Fragment key={index}>
                {pageNumber === '...' ? (
                  <span className="px-2 py-1 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageClick(pageNumber)}
                    className={`px-3 py-1 rounded ${
                      currentPage === pageNumber
                        ? 'bg-black text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
          
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="p-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )}
    </div>
  );
};

export default ManageShares;